/**
 * HomeButtons — the home-button (`sidebar_link`) row for the Cluster-1 Home
 * tabs (US15 / FR-060, D-122).
 *
 * Payload-driven, the sibling of `HomeCards`: each published `sidebar_link` is
 * read through `useHomeButtons()` (the `ContentReaderPort`) and rendered in its
 * assigned tab with its icon + two label lines. A `route`-target button
 * navigates to its member route; a `page`-target navigates to `/{linkSlug}`
 * (the same convention `HomeCards` uses). The icon identifier resolves to an
 * inline-SVG component via `resolveButtonIcon` (no icon dependency, D-043).
 *
 * State posture (P7): home buttons are a member-visible ADDITION, not core
 * content — while the read is pending or errors, and when the active tab has no
 * published buttons, the component renders NOTHING (the section is absent, never
 * an empty shell or an error strip that would clutter Home). Home stays
 * reachable regardless (FR-054); the curated-card strip owns the visible
 * loading/error affordance.
 *
 * The translator is passed in as a prop so the parent owns the `useTranslate`
 * subscription — keeps this component rendering pure with respect to i18n,
 * mirroring `HomeCards`.
 */

import type { JSX } from 'react';
import { useNavigate } from 'react-router';

import { resolveButtonIcon } from '../../components/cms/buttonIcons.js';
import type { HomeButton } from '../../content/ContentReaderPort.js';
import { useHomeButtons } from '../../content/hooks/useHomeButtons.js';
import { useLanguage } from '../../i18n/useLanguage.js';
import { useComposeWizard } from '../../member-messaging/compose/wizard/ComposeWizardProvider.js';
import { resolveContentPagePath } from '../../shell/contentPagePath.js';
import type { HomeCardsTab } from './HomeCards.js';

const listClasses = 'flex flex-col gap-3';
// Button row treatment — proto-parity for the Home button list: icon box +
// two-line body, the tappable-card shape shared with Home's quick-access rows.
const buttonClasses =
  'flex min-h-16 w-full items-center gap-3 rounded-xl bg-card text-card-foreground border border-border px-4 py-3 text-left ' +
  'cursor-pointer transition active:scale-95 ' +
  'hover:border-tab-active/30 hover:bg-tab-active/10 ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring';
const iconWrapperClasses =
  'flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-muted text-tab-active';
const iconClasses = 'h-6 w-6';
const bodyClasses = 'flex flex-1 flex-col items-start gap-0.5 overflow-hidden';
const labelClasses = 'text-base font-bold text-foreground';
const subtitleClasses = 'text-sm text-muted-foreground';

/**
 * Resolve a button's navigation path: a `route` target goes to its member
 * route verbatim; a `page` target resolves through the shared content
 * destination policy used by HomeCards.
 */
function buttonHref(button: HomeButton): string | null {
  switch (button.target.kind) {
    case 'route':
      return button.target.route;
    case 'page':
      return resolveContentPagePath(button.target.linkSlug);
    case 'contact':
      return null;
  }
}

interface ButtonViewProps {
  readonly button: HomeButton;
  readonly onClick: () => void;
}

function ButtonView({ button, onClick }: ButtonViewProps): JSX.Element {
  const Icon = resolveButtonIcon(button.icon);
  return (
    <button type="button" onClick={onClick} className={buttonClasses}>
      {Icon !== null ? (
        <span className={iconWrapperClasses}>
          <Icon className={iconClasses} />
        </span>
      ) : null}
      <span className={bodyClasses}>
        <span className={labelClasses}>{button.label}</span>
        <span className={subtitleClasses}>{button.subtitle}</span>
      </span>
    </button>
  );
}

/**
 * Render the home-button row for the given Home tab. Reads the published
 * buttons from `useHomeButtons()` and renders those whose `tab` matches.
 * Renders nothing while pending, on error, or when the tab has no buttons
 * (P7 — absent, not empty). Takes no translator: every visible string is
 * authored content (label / subtitle), and the non-populated states render
 * nothing.
 */
export function HomeButtons({ tab }: { readonly tab: HomeCardsTab }): JSX.Element | null {
  const navigate = useNavigate();
  const composeWizard = useComposeWizard();
  const { active: locale } = useLanguage();
  const buttons = useHomeButtons({ locale });

  if (buttons.isPending || buttons.isError) {
    return null;
  }

  const tabButtons = buttons.data.filter((button) => button.tab === tab);
  if (tabButtons.length === 0) {
    return null;
  }

  return (
    <div className={listClasses}>
      {tabButtons.map((button) => (
        <ButtonView
          key={button.id}
          button={button}
          onClick={() => {
            const href = buttonHref(button);
            if (href === null) {
              composeWizard.open();
              return;
            }
            navigate(href);
          }}
        />
      ))}
    </div>
  );
}
